import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  EMPTY_SITE_FILTER,
  useAddToRedList,
  useRemoveFromRedList,
  useSites,
  type SiteFilterQuery,
  type SiteRow,
} from "../../api/sites";
import "../../styles/sites.css";
import BulkBar from "./BulkBar";
import RegisterTable from "./RegisterTable";
import RegisterToolbar from "./RegisterToolbar";
import SectionTabs from "./SectionTabs";
import SiteDrawer from "./SiteDrawer";
import { filterFromSearch, pageFromSearch, pageRequest, rangeText, searchFromFilter, withPage } from "./siteFilter";
import StatusStrip from "./StatusStrip";

// The routing screen reads the same filter off its own URL.
export { filterFromSearch, searchFromFilter } from "./siteFilter";

/** Sites: the register, paged, under a status strip that filters and the
 * quick toggles; ticked rows get the bulk bar; a row opens its site in a
 * drawer at `/sites/:id`, so a site is a link and the register behind it keeps
 * its filter, page and ticks. */
export default function SitesPage() {
  const { id } = useParams();
  const selected = id === undefined || !/^\d+$/.test(id) ? null : Number(id);
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const filter = useMemo(() => filterFromSearch(params), [params]);
  const page = pageFromSearch(params);
  const list = useSites(filter, pageRequest(page));
  const [ticked, setTicked] = useState<ReadonlySet<number>>(new Set());
  const [done, setDone] = useState<string | null>(null);
  const addRed = useAddToRedList();
  const removeRed = useRemoveFromRedList();
  const redListError = addRed.error ?? removeRed.error;
  const redListPending = addRed.isPending
    ? (addRed.variables?.[0] ?? null)
    : removeRed.isPending
      ? (removeRed.variables?.[0] ?? null)
      : null;

  function redList(row: SiteRow, on: boolean) {
    setDone(null);
    addRed.reset();
    removeRed.reset();
    (on ? addRed : removeRed).mutate([row.id], {
      onSuccess: () => setDone(on ? `Put ${row.name} on the Red List.` : `Took ${row.name} off the Red List.`),
    });
  }

  const data = list.data;
  const rows = data?.rows ?? [];
  const office = data?.office ?? false;
  const query = params.toString();

  function setFilter(next: SiteFilterQuery, replace = false) {
    setDone(null);
    setParams(searchFromFilter(next), { replace });
  }

  function set<K extends keyof SiteFilterQuery>(key: K, value: SiteFilterQuery[K]) {
    setFilter({ ...filter, [key]: value });
  }

  function toggleStatus(status: string) {
    set("status", filter.status.includes(status) ? filter.status.filter((s) => s !== status) : [...filter.status, status]);
  }

  function tick(ids: readonly number[], on: boolean) {
    const next = new Set(ticked);
    for (const rowId of ids) {
      if (on) {
        next.add(rowId);
      } else {
        next.delete(rowId);
      }
    }
    setTicked(next);
  }

  const closeDrawer = useCallback(() => navigate(`/sites${query ? `?${query}` : ""}`), [navigate, query]);

  const exports = data ? (
    <>
      <a className="btn" href={data.exports.xlsx}>
        Export Excel
      </a>
      <a className="btn" href={data.exports.csv}>
        CSV
      </a>
    </>
  ) : undefined;

  return (
    <div className="page-bleed site-section">
      <SectionTabs sitesTo={`${location.pathname}${location.search}`} end={exports} />
      <div className="site-page">
        <StatusStrip
          statuses={data?.filters.statuses ?? []}
          counts={data?.counts ?? {}}
          selected={filter.status}
          onToggle={toggleStatus}
        />
        <RegisterToolbar
          filter={filter}
          register={data}
          onChange={(field, value) => set(field, value)}
          onSearch={(q) => setFilter({ ...filter, q }, true)}
          onClear={() => setFilter(EMPTY_SITE_FILTER)}
        />

        {office && ticked.size > 0 ? (
          <BulkBar
            ticked={ticked}
            providers={data?.filters.providers ?? []}
            onDone={(message) => {
              setDone(message);
              setTicked(new Set());
            }}
            onClear={() => setTicked(new Set())}
          />
        ) : null}
        {done ? (
          <p className="notice ok" role="status">
            {done}
          </p>
        ) : null}
        {redListError ? (
          <p className="notice bad" role="alert">
            {redListError.message}
          </p>
        ) : null}

        <section className="table-card site-register" aria-label="Sites" aria-busy={list.isFetching}>
          {list.isError ? (
            <p className="notice bad site-register-note" role="alert">
              Could not load the register: {list.error.message}
            </p>
          ) : null}
          {list.isPending ? (
            <p className="empty-state">Loading sites…</p>
          ) : rows.length === 0 && data !== undefined && data.total > 0 ? (
            <p className="empty-state">
              This page is past the last site.{" "}
              <button type="button" className="go-link" onClick={() => setParams(withPage(params, 0))}>
                Back to the first page
              </button>
            </p>
          ) : rows.length === 0 && !list.isError ? (
            <p className="empty-state">No sites match these filters.</p>
          ) : rows.length > 0 ? (
            <RegisterTable
              rows={rows}
              office={office}
              selected={selected}
              ticked={ticked}
              onTick={(rowId, on) => tick([rowId], on)}
              onTickPage={(on) => tick(rows.map((row) => row.id), on)}
              onOpen={(rowId) => navigate(`/sites/${rowId}${query ? `?${query}` : ""}`)}
              onRedList={redList}
              redListPending={redListPending}
            />
          ) : null}
          {data ? (
            <Pager
              offset={data.offset}
              shown={rows.length}
              total={data.total}
              onPage={(next) => setParams(withPage(params, next))}
              page={page}
            />
          ) : null}
        </section>
      </div>

      {selected === null ? null : <SiteDrawer id={selected} onClose={closeDrawer} />}
    </div>
  );
}

function Pager({
  offset,
  shown,
  total,
  page,
  onPage,
}: {
  offset: number;
  shown: number;
  total: number;
  page: number;
  onPage: (page: number) => void;
}) {
  const atEnd = offset + shown >= total;
  return (
    <nav className="pager" aria-label="Pages">
      <span className="pager-text">{rangeText(offset, shown, total)}</span>
      <button type="button" className="row-button" disabled={page === 0} onClick={() => onPage(page - 1)}>
        ‹ Previous
      </button>
      <button type="button" className="row-button" disabled={atEnd} onClick={() => onPage(page + 1)}>
        Next ›
      </button>
    </nav>
  );
}
